import { Component, OnInit, ElementRef, HostListener, inject } from '@angular/core';
import { Api } from '../../services/api';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from '../../services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { MdlManageBudget } from '../../components/mdl-manage-budget/mdl-manage-budget';
import { MdlDelete } from '../../components/mdl-delete/mdl-delete';
import { MdlChangeBudgetComponent } from '../../components/mdl-change-budget/mdl-change-budget';
import { PermissionService } from '../../services/permission.service';
import { MdlFilterBudgets } from '../../components/mdl-filter-budgets/mdl-filter-budgets';

@Component({
    selector:'app-budget',
    standalone:true,
    imports:[CommonModule,FormsModule],
    templateUrl:'./budget.html',
    styleUrls:['./budget.css']
})
export class Budget implements OnInit{

    presupuestos: any[] = []
    presupuestosFiltrados: any[] = [];
    isLoading: boolean = true;
    errorMessage: string = '';
    showRecentDropdown: boolean = false;
    selectedItem: string = 'todas';
    openedMenuId: string | null = null;
    searchTerm: string = '';
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';
    activeFilters: any = null;

    constructor(
        private api: Api,
        private elementRef: ElementRef,
        private router: Router,
        private alertService: AlertService,
        public permissionService: PermissionService
    ){
        this.cargarPresupuestos();
    }

    readonly dialog = inject(MatDialog)

    ngOnInit(): void {
        this.initializeFilters();
    }


    private initializeFilters(): void {
        if (!this.activeFilters) {
            this.activeFilters = {
                allSyncStatus: true,
                allDateRanges: true,
                priceRange: 'all',
                dateRange: 'all',
                syncStatus: {
                    sincronizado: false,
                    noSincronizado: false
                },
                dateRanges: {
                    proximos7dias: false,
                    proximos30dias: false,
                    vencidos: false
                }
            };
        }
    }

    openFilterModal(): void {
        const dialogRef = this.dialog.open(MdlFilterBudgets, {
            minWidth: '600px',
            maxHeight: '80vh',
            panelClass: 'mat-dlg-class',
            data: this.activeFilters 
        });

        dialogRef.afterClosed().subscribe((filters: any) => {
            if (filters) {
                this.activeFilters = filters;
                this.applyAdvancedFilters();
                this.alertService.success('Filtros aplicados', 'Filtros aplicados correctamente');
            }
        });
    }

    private applyAdvancedFilters(): void {
        let filteredBudgets = [...this.presupuestos];

        if (!this.activeFilters.allSyncStatus) {
            const selectedSyncStatus = Object.keys(this.activeFilters.syncStatus).filter(key => this.activeFilters.syncStatus[key]);
            if (selectedSyncStatus.length > 0) {
                filteredBudgets = filteredBudgets.filter(presupuesto => {
                    const syncStatus = this.mapSyncStatusToFilter(presupuesto.Sincronizacion);
                    return selectedSyncStatus.includes(syncStatus);
                });
            }
        }

        if (this.activeFilters.priceRange !== 'all') {
            filteredBudgets = this.filterByPriceRange(filteredBudgets, this.activeFilters.priceRange);
        }

        if (!this.activeFilters.allDateRanges) {
            const selectedDateRanges = Object.keys(this.activeFilters.dateRanges).filter(key => this.activeFilters.dateRanges[key]);
            if (selectedDateRanges.length > 0) {
                filteredBudgets = this.filterByDateRanges(filteredBudgets, selectedDateRanges);
            }
        }

        this.presupuestosFiltrados = filteredBudgets;
        
        if (this.sortColumn) {
            this.applySorting();
        }
    }

    private mapSyncStatusToFilter(sincronizacion: any): string {
        if (sincronizacion === true || sincronizacion === 1 || sincronizacion === 'true') {
            return 'sincronizado';
        }
        return 'noSincronizado';
    }

    private filterByPriceRange(budgets: any[], priceRange: string): any[] {
        return budgets.filter(presupuesto => {
            const precio = parseFloat(presupuesto.precio_total) || 0;
            
            switch (priceRange) {
                case '0-100000':
                    return precio >= 0 && precio <= 100000;
                case '100001-500000':
                    return precio >= 100001 && precio <= 500000;
                case '500001-1000000':
                    return precio >= 500001 && precio <= 1000000;
                case '1000001+':
                    return precio > 1000000;
                default:
                    return true;
            }
        });
    }

    private filterByDateRanges(budgets: any[], dateRanges: string[]): any[] {
        const hoy = new Date();
        return budgets.filter(presupuesto => {
            if (!presupuesto.fecha_caducidad) return false;
            
            const fechaCaducidad = new Date(presupuesto.fecha_caducidad);
            const diffDias = Math.floor((fechaCaducidad.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
            
            for (const range of dateRanges) {
                switch (range) {
                    case 'proximos7dias':
                        if (diffDias >= 0 && diffDias <= 7) return true;
                        break;
                    case 'proximos30dias':
                        if (diffDias >= 0 && diffDias <= 30) return true;
                        break;
                    case 'vencidos':
                        if (diffDias < 0) return true;
                        break;
                }
            }
            return false;
        });
    }

    sortByColumn(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }

        this.applySorting();
    }

    private applySorting(): void {
        if (!this.sortColumn) return;

        this.presupuestosFiltrados = [...this.presupuestosFiltrados].sort((a, b) => {
            let valueA = a[this.sortColumn];
            let valueB = b[this.sortColumn];

            switch (this.sortColumn) {
                case 'nombre_presupuesto':
                case 'oportunidades.nombre':
                    valueA = valueA?.toLowerCase() || '';
                    valueB = valueB?.toLowerCase() || '';
                    break;
                
                case 'subtotal':
                case 'precio_total':
                    valueA = parseFloat(valueA) || 0;
                    valueB = parseFloat(valueB) || 0;
                    break;
                
                case 'fecha_caducidad':
                    valueA = valueA ? new Date(valueA) : new Date(0);
                    valueB = valueB ? new Date(valueB) : new Date(0);
                    break;
                
                case 'Sincronizacion':
                    valueA = this.mapSyncStatusToFilter(valueA);
                    valueB = this.mapSyncStatusToFilter(valueB);
                    break;
                
                default:
                    if (typeof valueA === 'string') {
                        valueA = valueA.toLowerCase();
                        valueB = valueB.toLowerCase();
                    }
            }

            if (valueA < valueB) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    onSearchChange(): void {
        if (!this.searchTerm.trim()) {
            if (this.activeFilters) {
                this.applyAdvancedFilters();
            } else {
                this.presupuestosFiltrados = [...this.presupuestos];
            }
        } else {
            const searchLower = this.searchTerm.toLowerCase().trim();
            this.presupuestosFiltrados = this.presupuestos.filter(presupuesto => 
                presupuesto.nombre_presupuesto?.toLowerCase().includes(searchLower) ||
                presupuesto.oportunidades?.nombre?.toLowerCase().includes(searchLower) ||
                presupuesto.subtotal?.toString().includes(searchLower) ||
                presupuesto.precio_total?.toString().includes(searchLower) ||
                presupuesto.fecha_caducidad?.toLowerCase().includes(searchLower)
            );
        }
        
        if (this.sortColumn) {
            this.applySorting();
        }
    }

    hasActiveFilters(): boolean {
        if (!this.activeFilters) return false;
        const hasSyncFilter = !this.activeFilters.allSyncStatus && 
            Object.values(this.activeFilters.syncStatus).some(val => val === true);
        const hasDateFilter = !this.activeFilters.allDateRanges && 
            Object.values(this.activeFilters.dateRanges).some(val => val === true);
        const hasPriceFilter = this.activeFilters.priceRange !== 'all';
        
        return hasSyncFilter || hasDateFilter || hasPriceFilter;
    }

    clearFilters(): void {
        this.activeFilters = null;
        this.searchTerm = '';
        this.sortColumn = '';
        this.sortDirection = 'asc';
        this.initializeFilters();
        this.presupuestosFiltrados = [...this.presupuestos];
        this.alertService.success('Filtros limpiados', 'Los filtros han sido limpiados');
    }


    cargarPresupuestos():void{
        console.log('[DEBUG] cargarPresupuestos fue llamado');
        this.api.showLoading();
        this.isLoading = true;
        this.errorMessage = '';

        this.api.getBudgets().subscribe({
            next: (response: any) => {
                this.presupuestos = (response.presupuesto || response).map((presupuesto: any) => ({
                    ...presupuesto,
                    id: presupuesto.presupuesto_id || presupuesto.id,
                    propietarioId: presupuesto.ejecutivo_id
                }));

                if (this.activeFilters) {
                    this.applyAdvancedFilters();
                } else {
                    this.presupuestosFiltrados = [...this.presupuestos];
                }

                setTimeout(() => {
                    this.api.hideLoading();
                    this.isLoading = false;
                }, 500);
            },
            error: (error: any) => {
                console.error('Error al cargar presupuestos:', error);

                if (error.status === 403) {
                    this.errorMessage = 'No tienes permiso para ver presupuestos.';
                } else {
                    this.errorMessage = 'Error al cargar la lista de presupuesto.';
                }

                this.mostrarMensaje(this.errorMessage, 'error');

                setTimeout(() => {
                    this.api.hideLoading();
                    this.isLoading = false;
                }, 500);
            }
        });
    }


    openMdlCreateBudget() {
        this.dialog.open(MdlManageBudget,{
            minWidth: '800px',
            maxHeight: '900px',
            panelClass: 'mat-dlg-class',
            data:{
                mode: 'create'
            }
        })
    }

    updateBudget(data: any) {
        console.log("Click");
        this.dialog.open(MdlManageBudget,{
            minWidth: '800px',
            maxHeight: '900px',
            panelClass: 'mat-dlg-class',
            data:{
                mode: 'edit',
                budget: data
            }
        })
    }

    openMdlDeleteBudget(id:string) {
        this.dialog.open(MdlDelete, {
            minWidth: '500px',
            minHeight: '260px',
            panelClass: 'mat-dlg-class',
            data:{
                mode: 'budget',
                id: id
            }
        })
    }

    openMdlChangeOwner(presupuesto: any) {
        this.dialog.open(MdlChangeBudgetComponent, {
            minWidth: '750px',
            maxHeight: '900px',
            panelClass: 'mat-dlg-class',
            data: { budgetData: presupuesto }
        }).afterClosed().subscribe(result => {
            if (result) {
                console.log('Nuevo propietario:', result);
            }
        });
    }
    
    private esUUIDValido(id: string): boolean {
        const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return regex.test(id);
    }

    mostrarMensaje(mensaje: string, tipo: 'exito' | 'error' | 'advertencia'): void {
        switch (tipo) {
            case 'exito':
                this.alertService.success('Éxito', mensaje);
                break;
            case 'error':
                this.alertService.error('Error', mensaje);
                break;
            case 'advertencia':
                this.alertService.warning('Advertencia', mensaje);
                break;
        }
    }

    toggleRecentDropdown(): void {
        this.showRecentDropdown = !this.showRecentDropdown;
    }

    selectItem(key: string): void {
        this.selectedItem = key;
        this.showRecentDropdown = false; 
    }

    toggleActionsMenu(id: string): void {
        this.openedMenuId = this.openedMenuId === id ? null : id;
        this.showRecentDropdown = false; 
    }

    verDetallePresupuesto(id: string, event?: MouseEvent) {
        if (event) {
            const target = event.target as HTMLElement;
            const isCheckbox = target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox';
            const isDropdown = target.closest('.dropdown-container') !== null;
            
            if (isCheckbox || isDropdown) {
                return;
            }
        }

        if (!id || !this.esUUIDValido(id)) {
            this.mostrarMensaje('ID de prospecto no válido', 'error');
            return;
        }

        this.router.navigate(['budget', id]).then(navigationResult => {
            if (!navigationResult) {
                this.mostrarMensaje('No se pudo cargar el detalle del presupuesto', 'error');
                console.error('Error de navegación a prospects/', id);
            }
        });
    }

    @HostListener('document:click', ['$event.target'])
    onClickOutside(target: EventTarget | null): void {
        if (!(target instanceof HTMLElement)) return;
        
        const clickedInsideRecent = this.elementRef.nativeElement.querySelector('.dropdown-trigger')?.contains(target);
        const clickedInsideActions = this.elementRef.nativeElement.querySelector('.dots-button')?.contains(target);
        
        if (!clickedInsideRecent) {
            this.showRecentDropdown = false;
        }
        
        if (!clickedInsideActions) {
            this.openedMenuId = null;
        }
    }
}